const STORAGE_KEY = "pep-affiliate-ref";
const RECRUIT_STORAGE_KEY = "pep-affiliate-recruit";
const OWN_CODE_KEY = "pep-affiliate-own-code";
const MANUAL_CODE_KEY = "pep-affiliate-ref-manual";
const REF_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export interface StoredAffiliateRef {
  code: string;
  product?: string;
  landedAt: string;
  landingPath: string;
}

export interface StoredAffiliateRecruit {
  code: string;
  landedAt: string;
  landingPath: string;
}

function isRefExpired(landedAt: string): boolean {
  const t = Date.parse(landedAt);
  if (Number.isNaN(t)) return true;
  return Date.now() - t > REF_TTL_MS;
}

export function readAffiliateRefFromSearch(search: string): string | null {
  const params = new URLSearchParams(search);
  return params.get("ref") ?? params.get("affiliate") ?? null;
}

export function readAffiliateRecruitFromSearch(search: string): string | null {
  return new URLSearchParams(search).get("recruit");
}

/** First-touch : conserve le premier code valide 30 jours (localStorage). */
export function captureAffiliateRef(search: string, pathname: string): void {
  const code = readAffiliateRefFromSearch(search);
  if (!code?.trim()) return;

  const normalized = code.trim().toUpperCase();
  const existing = getStoredAffiliateRef();
  if (existing && !isRefExpired(existing.landedAt)) return;

  const payload: StoredAffiliateRef = {
    code: normalized,
    product: new URLSearchParams(search).get("product") ?? undefined,
    landedAt: new Date().toISOString(),
    landingPath: pathname,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

/** Recrutement ambassadeur — distinct du parrainage client (?ref=). */
export function captureAffiliateRecruitRef(search: string, pathname: string): void {
  const code = readAffiliateRecruitFromSearch(search);
  if (!code?.trim()) return;

  const normalized = code.trim().toUpperCase();
  const existing = getStoredAffiliateRecruit();
  if (existing && !isRefExpired(existing.landedAt)) return;

  const payload: StoredAffiliateRecruit = {
    code: normalized,
    landedAt: new Date().toISOString(),
    landingPath: pathname,
  };
  localStorage.setItem(RECRUIT_STORAGE_KEY, JSON.stringify(payload));
}

export function captureAffiliateAttribution(search: string, pathname: string): void {
  captureAffiliateRecruitRef(search, pathname);
  captureAffiliateRef(search, pathname);
}

export function getStoredAffiliateRef(): StoredAffiliateRef | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAffiliateRef;
    if (isRefExpired(parsed.landedAt)) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function getStoredAffiliateRecruit(): StoredAffiliateRecruit | null {
  try {
    const raw = localStorage.getItem(RECRUIT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAffiliateRecruit;
    if (isRefExpired(parsed.landedAt)) {
      localStorage.removeItem(RECRUIT_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function getStoredRecruiterCode(): string | undefined {
  return getStoredAffiliateRecruit()?.code;
}

export function getAffiliateCodeForLead(): string | undefined {
  const manual = localStorage.getItem(MANUAL_CODE_KEY)?.trim();
  if (manual) return manual.toUpperCase();
  return getStoredAffiliateRef()?.code;
}

export function setManualAffiliateCode(code: string | null): void {
  if (!code?.trim()) {
    localStorage.removeItem(MANUAL_CODE_KEY);
    return;
  }
  localStorage.setItem(MANUAL_CODE_KEY, code.trim().toUpperCase());
}

export function getManualAffiliateCode(): string | null {
  return localStorage.getItem(MANUAL_CODE_KEY);
}

export function saveOwnAffiliateCode(code: string) {
  localStorage.setItem(OWN_CODE_KEY, code.trim().toUpperCase());
}

export function getOwnAffiliateCode(): string | null {
  return localStorage.getItem(OWN_CODE_KEY);
}

export function buildAffiliateUrl(
  baseUrl: string,
  path: string,
  code: string,
  extraParams?: Record<string, string>,
): string {
  const url = new URL(path, baseUrl.replace(/\/$/, "") + "/");
  url.searchParams.set("ref", code.trim().toUpperCase());
  if (extraParams) {
    for (const [k, v] of Object.entries(extraParams)) {
      url.searchParams.set(k, v);
    }
  }
  return url.toString();
}

export function buildRecruitUrl(baseUrl: string, code: string): string {
  const url = new URL("/affiliation/inscription", baseUrl.replace(/\/$/, "") + "/");
  url.searchParams.set("recruit", code.trim().toUpperCase());
  return url.toString();
}

export function buildWhatsAppShareUrl(message: string): string {
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
}

export function buildFacebookShareUrl(url: string): string {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
}

export function applyShareTemplate(template: string, link: string): string {
  return template.replace(/\{\{link\}\}/g, link);
}
